import axios from 'axios';
import { toast } from 'react-toastify';
import { getUserFriendlyMessage, logError, isAuthError } from '../utils/errorHandler';

// Rate limiting configuration - COMMENTED OUT
// const RATE_LIMIT_CONFIG = {
//   maxRetries: 3,
//   baseDelay: 1000, // 1 second
//   maxDelay: 30000, // 30 seconds
//   retryableStatuses: [429, 503, 504], // Status codes that can be retried
// };

// Request deduplication system
class RequestDeduplicator {
  public pendingRequests: Map<string, Promise<any>> = new Map();

  async deduplicate<T>(
    key: string,
    requestFn: () => Promise<T>
  ): Promise<T> {
    // If there's already a pending request with the same key, return it
    if (this.pendingRequests.has(key)) {
      console.log(`🔄 Deduplicating request: ${key}`);
      return this.pendingRequests.get(key)!;
    }

    // Create new request
    const requestPromise = requestFn().finally(() => {
      // Remove from pending requests when completed
      this.pendingRequests.delete(key);
    });

    // Store the pending request
    this.pendingRequests.set(key, requestPromise);

    return requestPromise;
  }

  // Generate a unique key for API requests
  generateKey(method: string, url: string, params?: any): string {
    const paramString = params ? JSON.stringify(params) : '';
    return `${method.toUpperCase()}:${url}:${paramString}`;
  }

  // Check if a request is currently pending
  isPending(key: string): boolean {
    return this.pendingRequests.has(key);
  }

  // Get count of pending requests
  getPendingCount(): number {
    return this.pendingRequests.size;
  }

  // Clear all pending requests (useful for cleanup)
  clear(): void {
    this.pendingRequests.clear();
  }
}

// Global request deduplicator instance
const requestDeduplicator = new RequestDeduplicator();

// Simple cache for clinic branding to prevent repeated calls
let clinicBrandingCache: { data: any; timestamp: number } | null = null;
const CACHE_DURATION = 30000; // 30 seconds cache

// Retry utility with exponential backoff - COMMENTED OUT
// const retryWithBackoff = async (fn: () => Promise<any>, retries: number = 0): Promise<any> => {
//   try {
//     return await fn();
//   } catch (error: any) {
//     if (retries >= RATE_LIMIT_CONFIG.maxRetries || 
//         !RATE_LIMIT_CONFIG.retryableStatuses.includes(error.response?.status)) {
//       throw error;
//     }

//     const delay = Math.min(
//       RATE_LIMIT_CONFIG.baseDelay * Math.pow(2, retries),
//       RATE_LIMIT_CONFIG.maxDelay
//     );

//     console.log(`Rate limited. Retrying in ${delay}ms... (attempt ${retries + 1}/${RATE_LIMIT_CONFIG.maxRetries})`);

//     await new Promise(resolve => setTimeout(resolve, delay));
//     return retryWithBackoff(fn, retries + 1);
//   }
// };

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  timeout: 120000, // 120 seconds - heavy report endpoints need longer processing time
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token') || localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    try {
      const { response, config, code, message } = error;

      // Check if browser is offline
      const isOffline = !navigator.onLine;

      // Detect network-related errors
      const isNetworkError =
        isOffline ||
        code === 'ERR_NETWORK' ||
        code === 'ERR_INTERNET_DISCONNECTED' ||
        code === 'ERR_NETWORK_CHANGED' ||
        message?.includes('Network Error') ||
        message?.includes('Failed to fetch') ||
        message?.includes('network error') ||
        (error.request && !error.response); // Request made but no response received

      // Handle offline/network errors gracefully
      if (isNetworkError || isOffline) {
        const offlineMessage = isOffline
          ? 'You are currently offline. Please check your internet connection.'
          : 'Network connection error. Please check your internet connection and try again.';

        // Only show toast if not already showing (prevent spam)
        const existingToasts = document.querySelectorAll('.Toastify__toast');
        const hasOfflineToast = Array.from(existingToasts).some(toast =>
          toast.textContent?.includes('offline') || toast.textContent?.includes('connection')
        );

        if (!hasOfflineToast) {
          toast.error(offlineMessage, {
            autoClose: isOffline ? false : 8000, // Keep offline toast visible until connection restored
            position: 'top-center',
            style: {
              background: '#fee2e2',
              color: '#dc2626',
              border: '1px solid #fecaca',
            },
            toastId: 'network-error', // Use same ID to prevent duplicates
          });
        }

        // Return a structured error that components can handle
        return Promise.reject({
          ...error,
          isNetworkError: true,
          isOffline,
          userMessage: offlineMessage,
        });
      }

      // Skip logging and toasting for 404 errors on scheduled counts endpoint
      // This endpoint may not be available until backend is restarted
      if (response?.status === 404 && config?.url?.includes('/workflows/counts/scheduled')) {
        return Promise.reject(error);
      }

      // Log error for debugging (skip for network errors to reduce noise)
      try {
        logError(error, 'API Interceptor');
      } catch (logErr) {
        console.error('API Error:', error);
      }

      // Handle 401 Unauthorized
      if (response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(error);
      }

      // For 400 errors (validation errors), let the component handle the error display
      // This allows components to show more specific and contextual error messages
      if (response?.status === 400) {
        return Promise.reject(error);
      }

      // Handle timeout errors specifically
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        const timeoutMessage = 'Request timed out. Please try again or check your connection.';
        toast.error(timeoutMessage, {
          autoClose: 5000,
          position: 'top-center',
          style: {
            background: '#fee2e2',
            color: '#dc2626',
            border: '1px solid #fecaca',
          },
        });
        return Promise.reject(error);
      }

      // Handle other errors with enhanced error messages
      const errorMessage = getUserFriendlyMessage ? getUserFriendlyMessage(error) : (error.response?.data?.message || error.message || 'An error occurred');

      // Show error toast with appropriate styling
      toast.error(errorMessage, {
        autoClose: 5000,
        position: 'top-center',
        style: {
          background: '#fee2e2',
          color: '#dc2626',
          border: '1px solid #fecaca',
        },
      });

      return Promise.reject(error);
    } catch (interceptorError) {
      // If the interceptor itself fails, just reject the original error
      console.error('API Interceptor error:', interceptorError);
      console.error('Original error:', error);
      console.error('Interceptor error details:', {
        message: interceptorError?.message,
        stack: interceptorError?.stack,
        name: interceptorError?.name,
        toString: String(interceptorError)
      });
      return Promise.reject(error);
    }
  }
);

// Enhanced API wrapper with retry logic and deduplication - COMMENTED OUT
// const createAPIWithRetry = <T extends any[]>(apiCall: (...args: T) => Promise<any>) => {
//   return async (...args: T) => {
//     try {
//       return await retryWithBackoff(() => apiCall(...args));
//     } catch (error: any) {
//       // If it's a rate limit error, don't retry again
//       if (error.response?.status === 429) {
//         throw error;
//       }
//       throw error;
//     }
//   };
// };

// Simple API wrapper with deduplication
const createAPIWithDeduplication = <T extends any[]>(
  method: string,
  apiCall: (...args: T) => Promise<any>
) => {
  return async (...args: T) => {
    // Generate unique key for this request
    const url = args[0] || '';
    const params = args[1] || {};
    const key = requestDeduplicator.generateKey(method, url, params);

    return requestDeduplicator.deduplicate(key, () => apiCall(...args));
  };
};

// Rate limit utility functions - DISABLED (always returns no rate limiting)
export const rateLimitUtils = {
  // Check if user is currently rate limited - DISABLED
  isRateLimited: (): boolean => {
    return false; // Rate limiting is disabled
  },

  // Get remaining time until rate limit expires - DISABLED
  getRemainingTime: (): number => {
    return 0; // No rate limiting, so no remaining time
  },

  // Get user-friendly message about rate limit - DISABLED
  getRateLimitMessage: (): string | null => {
    return null; // No rate limiting messages
  },

  // Clear rate limit info (useful for testing) - DISABLED
  clearRateLimitInfo: (): void => {
    // No-op since rate limiting is disabled
  },

  // Check if retry is recommended - DISABLED
  shouldRetry: (error: any): boolean => {
    return false; // No retry logic when rate limiting is disabled
  },

  // Request deduplication utilities - DISABLED
  requestDeduplicator: {
    // Check if a specific request is pending - DISABLED
    isRequestPending: (method: string, url: string, params?: any): boolean => {
      return false; // No request deduplication
    },

    // Get count of all pending requests - DISABLED
    getPendingRequestCount: (): number => {
      return 0; // No pending requests tracked
    },

    // Clear all pending requests - DISABLED
    clearAllPending: (): void => {
      // No-op since deduplication is disabled
    },

    // Get all pending request keys (for debugging) - DISABLED
    getPendingRequestKeys: (): string[] => {
      return []; // No pending requests
    }
  }
};

// Auth API
export const authAPI = {
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),

  register: (userData: any) =>
    api.post('/auth/register', userData),

  getProfile: () =>
    api.get('/auth/profile'),

  updateProfile: (profileData: any) =>
    api.put('/auth/profile', profileData),

  changePassword: (passwordData: { currentPassword: string; newPassword: string }) =>
    api.put('/auth/change-password', passwordData),

  getUsers: () =>
    api.get('/auth/users'),

  toggleUserStatus: (userId: string) =>
    api.patch(`/auth/users/${userId}/toggle-status`),
};

// Patient API
export const patientAPI = {
  getDashboard: () =>
    api.get('/patients/dashboard'),

  getPatients: (params?: any) => api.get('/patients', { params }),
  getPatientReports: (params?: any) => api.get('/patients/reports', { params }),
  getPatient: (id: string) => api.get(`/patients/${id}`),
  createPatient: (data: any) => api.post('/patients', data),
  updatePatient: (id: string, data: any) => api.put(`/patients/${id}`, data),
  deletePatient: (id: string) => api.delete(`/patients/${id}`),
  searchPatients: (params: any) => api.get('/patients/search', { params }),
  updateInsuranceStatus: (id: string, status: string) => api.put(`/patients/${id}/insurance-policy/status`, { status }),

  // Update orders status for a patient; dates optional (when omitted, updates all)
  updateOrdersStatus: (id: string, data: { startDate?: string; endDate?: string; status: string }) =>
    api.put(`/patients/${id}/orders/status`, data),

  // Enhanced Patient API methods
  getEnhancedPatients: (params?: any) => api.get('/patients/enhanced', { params }),
  exportPatients: (params?: any) => api.get('/patients/export', {
    params,
    responseType: 'blob' // Important for file downloads
  }),
  getPatientAnalytics: (params?: any) => api.get('/patients/analytics', { params }),
  getInsuranceAnalytics: (params?: any) => api.get('/patients/insurance-analytics', { params }),

  getInsurancePolicies: (patientId: string) =>
    api.get(`/patients/${patientId}/insurance-policies`),

  addInsurancePolicy: (patientId: string, policyData: any) =>
    api.post(`/patients/${patientId}/insurance-policies`, policyData),

  getMedicalHistory: (patientId: string) =>
    api.get(`/patients/${patientId}/medical-history`),

  upsertMedicalHistory: (patientId: string, data: any) =>
    api.post(`/patients/${patientId}/medical-history`, data),

  getBulkRegistrationTemplate: () =>
    api.get('/patients/bulk-registration'),

  bulkRegisterPatients: (patients: any[]) =>
    api.post('/patients/bulk-registration', { patients }),

  // Check policy number details for real-time validation
  checkPolicyNumber: (policyNumber: string) =>
    api.get(`/patients/check-policy/${policyNumber}`),

  // Bulk operations and queue status endpoints removed - not implemented in backend
};

// Department API
export const departmentAPI = {
  getDepartments: (params?: any) =>
    api.get('/admin/departments', { params }),

  getPublicDepartments: () =>
    api.get('/admin/departments/public'),

  getDepartment: (id: string) =>
    api.get(`/admin/departments/${id}`),

  createDepartment: (data: any) =>
    api.post('/admin/departments', data),

  updateDepartment: (id: string, data: any) =>
    api.put(`/admin/departments/${id}`, data),

  deleteDepartment: (id: string) =>
    api.delete(`/admin/departments/${id}`),
};

// Doctor API
export const doctorAPI = {
  getDoctors: (params?: any) =>
    api.get('/doctors', { params }),

  getDoctor: (id: string) =>
    api.get(`/doctors/${id}`),

  createDoctor: (doctorData: any) =>
    api.post('/doctors', doctorData),

  updateDoctor: (id: string, doctorData: any) =>
    api.put(`/doctors/${id}`, doctorData),

  deleteDoctor: (id: string) =>
    api.delete(`/doctors/${id}`),

  searchDoctors: (query: string) =>
    api.get('/doctors/search', { params: { q: query } }),

  getSchedule: (doctorId: string) =>
    api.get(`/doctors/${doctorId}/schedule`),

  // Doctor Schedule Management
  getDoctorSchedule: (doctorId: string) =>
    api.get(`/doctors/${doctorId}/schedule`),

  createDoctorSchedule: (doctorId: string, scheduleData: any) =>
    api.post(`/doctors/${doctorId}/schedule`, scheduleData),

  updateDoctorSchedule: (doctorId: string, scheduleData: any) =>
    api.put(`/doctors/${doctorId}/schedule`, scheduleData),

  deleteDoctorSchedule: (doctorId: string, scheduleId: string) =>
    api.delete(`/doctors/${doctorId}/schedule/${scheduleId}`),

  getDoctorAvailability: (doctorId: string, date: string) =>
    api.get(`/doctors/${doctorId}/availability?date=${date}`),

  // Department Assignment
  assignDepartment: (doctorId: string, departmentId: string, isPrimary: boolean = false) =>
    api.post(`/doctors/${doctorId}/departments`, { departmentId, isPrimary }),

  removeDepartmentAssignment: (doctorId: string, assignmentId: string) =>
    api.delete(`/doctors/${doctorId}/departments/${assignmentId}`),

  setPrimaryDepartment: (doctorId: string, assignmentId: string) =>
    api.patch(`/doctors/${doctorId}/departments/${assignmentId}/primary`),

  updateSchedule: (doctorId: string, scheduleData: any) =>
    api.post(`/doctors/${doctorId}/schedule`, scheduleData),

  getAvailability: (doctorId: string, date: string) =>
    api.get(`/doctors/${doctorId}/availability`, { params: { date } }),

  getDashboard: () =>
    api.get('/doctors/dashboard'),
};

// Appointment API
export const appointmentAPI = {
  getAppointments: (params?: any) =>
    api.get('/appointments', { params }),

  getAppointment: (id: string) =>
    api.get(`/appointments/${id}`),

  createAppointment: (appointmentData: any) =>
    api.post('/appointments', appointmentData),

  updateAppointment: (id: string, appointmentData: any) =>
    api.put(`/appointments/${id}`, appointmentData),

  cancelAppointment: (id: string, reason?: string) =>
    api.patch(`/appointments/${id}/cancel`, { reason }),

  getCalendar: (params: any) =>
    api.get('/appointments/calendar', { params }),
};

// Treatment API
export const treatmentAPI = {
  getTreatments: (params?: any) =>
    api.get('/treatments', { params }),

  getTreatment: (id: string) =>
    api.get(`/treatments/${id}`),

  createTreatment: (treatmentData: any) =>
    api.post('/treatments', treatmentData),

  updateTreatment: (id: string, treatmentData: any) =>
    api.put(`/treatments/${id}`, treatmentData),

  deleteTreatment: (id: string) =>
    api.delete(`/treatments/${id}`),

  addPrescription: (treatmentId: string, prescriptionData: any) =>
    api.post(`/treatments/${treatmentId}/prescriptions`, prescriptionData),

  addProcedure: (treatmentId: string, procedureData: any) =>
    api.post(`/treatments/${treatmentId}/procedures`, procedureData),

  searchTreatments: (query: string) =>
    api.get('/treatments/search', { params: { q: query } }),
};

// Treatment Templates API
export const treatmentTemplateAPI = {
  getTemplates: (params?: any) =>
    api.get('/treatment-templates', { params }),

  getTemplate: (id: string) =>
    api.get(`/treatment-templates/${id}`),

  createTemplate: (templateData: any) =>
    api.post('/treatment-templates', templateData),

  updateTemplate: (id: string, templateData: any) =>
    api.put(`/treatment-templates/${id}`, templateData),

  deleteTemplate: (id: string) =>
    api.delete(`/treatment-templates/${id}`),

  getCategories: () =>
    api.get('/treatment-templates/categories'),

  getDepartments: () =>
    api.get('/treatment-templates/departments'),
};

// Billing API
export const billingAPI = {
  getBills: (params?: any) =>
    api.get('/billing', { params }),

  getBill: (id: string) =>
    api.get(`/billing/${id}`),

  createBill: (billData: any) =>
    api.post('/billing', billData),

  updateBill: (id: string, billData: any) =>
    api.put(`/billing/${id}`, billData),

  getEstimate: (estimateData: any) =>
    api.post('/billing/estimate', estimateData),

  recordPayment: (billId: string, paymentData: any) =>
    api.post(`/billing/${billId}/payment`, paymentData),

  getOverdueBills: () =>
    api.get('/billing/overdue'),
};

// Claims API
export const claimAPI = {
  getClaims: (params?: any) =>
    api.get('/claims', { params }),

  getClaim: (id: string) =>
    api.get(`/claims/${id}`),

  createClaim: (claimData: any) =>
    api.post('/claims', claimData),

  updateClaim: (id: string, claimData: any) =>
    api.put(`/claims/${id}`, claimData),

  submitClaim: (id: string) =>
    api.post(`/claims/${id}/submit`),

  updateClaimStatus: (id: string, statusData: any) =>
    api.put(`/claims/${id}/status`, statusData),

  downloadClaim: (id: string) =>
    api.get(`/claims/${id}/download`, { responseType: 'blob' }),

  exportClaims: (params: any) =>
    api.get('/claims/export', { params, responseType: 'blob' }),

  preAuthorization: (preAuthData: any) =>
    api.post('/claims/pre-authorization', preAuthData),

  // Monthly Batch Claims
  generateMonthlyBatch: (batchData: any) =>
    api.post('/claims/monthly-batch', batchData),

  downloadMonthlyBatch: (batchNumber: string) =>
    api.get(`/claims/monthly-batch/${batchNumber}/download`, { responseType: 'blob' }),

  submitMonthlyBatch: (batchNumber: string) =>
    api.post(`/claims/monthly-batch/${batchNumber}/submit`),
};

// Admission API
export const admissionAPI = {
  getAdmissions: (params?: any) =>
    api.get('/admissions', { params }),

  getAdmission: (id: string) =>
    api.get(`/admissions/${id}`),

  createAdmission: (admissionData: any) =>
    api.post('/admissions', admissionData),

  createEnhancedAdmission: (admissionData: any) =>
    api.post('/admissions/enhanced', admissionData),

  updateAdmission: (id: string, admissionData: any) =>
    api.put(`/admissions/${id}`, admissionData),

  dischargePatient: (id: string, dischargeData: any) =>
    api.post(`/admissions/${id}/discharge`, dischargeData),

  reopenAdmission: (id: string) =>
    api.post(`/admissions/${id}/reopen`),


  getWards: () =>
    api.get('/admissions/wards'),

  getWardBeds: (wardId: string) =>
    api.get(`/admissions/wards/${wardId}/beds`),

  // Comprehensive search endpoints
  searchPatients: (params: { q: string; by?: 'name' | 'phone' | 'bed' }) =>
    api.get('/admissions/patients/search', { params }),

  getOccupiedBeds: (wardId: string) =>
    api.get(`/admissions/wards/${wardId}/occupied-beds`),

  getRooms: (wardId: string) =>
    api.get(`/admissions/wards/${wardId}/rooms`),

  getRoomsByWard: (wardId: string) =>
    api.get(`/admissions/wards/${wardId}/beds`),

  getBeds: (roomId: string) =>
    api.get(`/admissions/rooms/${roomId}/beds`),

  getBedPatient: (bedId: string) =>
    api.get(`/admissions/beds/${bedId}/patient`),

  getPatientsByRoom: (roomId: string) =>
    api.get(`/admissions?bedId=${roomId}&status=ADMITTED`),

  getWardAdmissions: (wardId: string) =>
    api.get(`/admissions/wards/${wardId}/admissions`),

  getStatistics: () =>
    api.get('/admissions/statistics'),

  transferPatient: (id: string, transferData: any) =>
    api.post(`/admissions/${id}/transfer`, transferData),

  getTransferHistory: (admissionId: string) =>
    api.get(`/admissions/${admissionId}/transfers`),

  getAvailableOTBeds: () =>
    api.get('/admissions/ot/available'),

  setupOTComplex: (setupData?: { wardName?: string; numberOfRooms?: number; pricePerDay?: number }) =>
    api.post('/admissions/ot/setup', setupData),

  getBedOccupancyReport: () =>
    api.get('/admissions/reports/bed-occupancy'),

  getAdmissionsFinancialSummary: (params: {
    dateFrom: string;
    dateTo: string;
    insuranceProviderId?: string;
    page?: number;
    limit?: number;
  }) => api.get('/admissions/reports/financial-summary', { params }),

  getOTQueue: () =>
    api.get('/admissions/ot/queue'),

  updateOTStatus: (admissionId: string, statusData: { status: string; notes?: string; returnToWard?: boolean; returnWardId?: string; returnBedId?: string; }) =>
    api.patch(`/admissions/${admissionId}/ot/status`, statusData),

  updateAdmissionStatus: (id: string, statusData: any) =>
    api.patch(`/admissions/${id}/status`, statusData),

  getCostBreakdown: (id: string) =>
    api.get(`/admissions/${id}/cost-breakdown`),

  getPatientCostBreakdown: (patientId: string, params?: { startDate?: string; endDate?: string }) =>
    api.get(`/admissions/patient/${patientId}/cost-breakdown`, { params }),

  getPatientSpendingReports: (queryString?: string) =>
    api.get(`/admissions/reports/patient-spending${queryString ? `?${queryString}` : ''}`),

  // Timeline
  getTimeline: (admissionId: string, params?: { page?: number; limit?: number; type?: string; since?: string }) =>
    api.get(`/admissions/${admissionId}/timeline`, { params }),

  // Vital Signs
  getVitalSigns: (admissionId: string) =>
    api.get(`/admissions/${admissionId}/vital-signs`),

  addVitalSigns: (admissionId: string, vitalData: any) =>
    api.post(`/admissions/${admissionId}/vital-signs`, vitalData),

  // Daily Rounds
  getDailyRounds: (admissionId: string) =>
    api.get(`/admissions/${admissionId}/daily-rounds`),

  addDailyRound: (admissionId: string, roundData: any) =>
    api.post(`/admissions/${admissionId}/daily-rounds`, roundData),

  // Medication Management
  getMedications: (admissionId: string) =>
    api.get(`/admissions/${admissionId}/medications`),

  addMedication: (admissionId: string, medicationData: any) =>
    api.post(`/admissions/${admissionId}/medications`, medicationData),

  updateMedication: (admissionId: string, medicationId: string, updateData: any) =>
    api.patch(`/admissions/${admissionId}/medications/${medicationId}`, updateData),

  recordMedicationAdministration: (admissionId: string, medicationId: string, administrationData: any) =>
    api.post(`/admissions/${admissionId}/medications/${medicationId}/administration`, administrationData),

  getMedicationSchedule: (admissionId: string) =>
    api.get(`/admissions/${admissionId}/medications/schedule`),

  getDispensings: (admissionId: string) =>
    api.get(`/admissions/${admissionId}/dispensings`),

  dispenseMedication: (admissionId: string, dispensingId: string, dispensingData: any) =>
    api.post(`/admissions/${admissionId}/dispensings/${dispensingId}/dispense`, dispensingData),

  getAdmissionServices: (id: string) =>
    api.get(`/admissions/${id}/services`),

  addAdmissionService: (id: string, serviceData: any) =>
    api.post(`/admissions/${id}/services`, serviceData),

  createAdmissionRequest: (requestData: any) =>
    api.post('/admissions/requests', requestData),

  getAdmissionRequests: (params?: any) =>
    api.get('/admissions/requests', { params }),

  updateAdmissionRequestStatus: (id: string, status: string, notes?: string) =>
    api.patch(`/admissions/requests/${id}/status`, { status, notes }),

  // Ward Management
  createWard: (wardData: any) =>
    api.post('/admissions/wards', wardData),

  updateWard: (id: string, wardData: any) =>
    api.put(`/admissions/wards/${id}`, wardData),

  deleteWard: (id: string) =>
    api.delete(`/admissions/wards/${id}`),

  getWardStats: () =>
    api.get('/admissions/wards/statistics'),

  getWardOccupancy: (wardId: string) =>
    api.get(`/admissions/wards/${wardId}/occupancy`),

  // Bed Management
  createBed: (wardId: string, bedData: any) =>
    api.post(`/wards/${wardId}/beds`, bedData),

  updateBed: (bedId: string, bedData: any) =>
    api.put(`/beds/${bedId}`, bedData),

  updateBedStatus: (bedId: string, status: string, notes?: string) =>
    api.patch(`/beds/${bedId}/status`, { status, notes }),

  assignPatientToBed: (bedId: string, patientId: string, admissionData: any) =>
    api.post(`/beds/${bedId}/assign`, { patientId, ...admissionData }),

  dischargeBed: (bedId: string, dischargeData: any) =>
    api.post(`/beds/${bedId}/discharge`, dischargeData),

  // Enhanced Admission APIs
  getAdmissionStatistics: () =>
    api.get('/admissions/statistics'),

  getDailyReport: (date?: string) =>
    api.get('/admissions/reports/daily', { params: date ? { date } : {} }),

  getDoctorAdmissions: (doctorId: string, params?: any) =>
    api.get(`/admissions/doctors/${doctorId}`, { params }),

  getCostEstimate: (estimateData: any) =>
    api.post('/admissions/cost-estimate', estimateData),

  // Update doctor assignment (legacy - single doctor)
  updateDoctor: (id: string, doctorId: string) =>
    api.put(`/admissions/${id}/doctor`, { doctorId }),

  // Multiple doctors management
  addDoctor: (id: string, doctorId: string, notes?: string) =>
    api.post(`/admissions/${id}/doctors`, { doctorId, notes }),

  removeDoctor: (id: string, doctorId: string) =>
    api.delete(`/admissions/${id}/doctors/${doctorId}`),

  getAssignedDoctors: (id: string) =>
    api.get(`/admissions/${id}/doctors`),
};

// Admin API
export const adminAPI = {
  getDashboard: () =>
    api.get('/admin/dashboard'),

  getAuditLogs: (params?: any) =>
    api.get('/admin/audit-logs', { params }),

  getAuditLogStats: () =>
    api.get('/admin/audit-logs/stats'),

  cleanAuditLogs: () =>
    api.delete('/admin/audit-logs/cleanup'),

  cleanAllAuditLogs: () =>
    api.delete('/admin/audit-logs/cleanup-all'),

  getSystemConfig: () =>
    api.get('/admin/system-config'),

  updateSystemConfig: (configData: any) =>
    api.post('/admin/system-config', configData),

  // Enhanced System Settings
  getSystemSettings: (params?: any) =>
    api.get('/admin/system-settings', { params }),

  updateSystemSetting: (key: string, settingData: any) =>
    api.put(`/admin/system-settings/${key}`, settingData),

  deleteSystemSetting: (key: string) =>
    api.delete(`/admin/system-settings/${key}`),

  bulkUpdateSystemSettings: (settingsData: any) =>
    api.post('/admin/system-settings/bulk', settingsData),

  getDepartments: () =>
    api.get('/admin/departments'),

  getDepartmentsPublic: () =>
    api.get('/admin/departments/public'),

  createDepartment: (departmentData: any) =>
    api.post('/admin/departments', departmentData),

  updateDepartment: (id: string, departmentData: any) =>
    api.put(`/admin/departments/${id}`, departmentData),

  deleteDepartment: (id: string) =>
    api.delete(`/admin/departments/${id}`),

  getInsuranceProviders: () =>
    api.get('/admin/insurance-providers'),

  getInsuranceCategories: () =>
    api.get('/insurance/categories'),

  createInsuranceProvider: (providerData: any) =>
    api.post('/admin/insurance-providers', providerData),

  getProcedureTypes: (params?: any) =>
    api.get('/admin/procedure-types', { params }),

  createProcedureType: (procedureData: any) =>
    api.post('/admin/procedure-types', procedureData),

  createMedicine: (medicineData: any) =>
    api.post('/admin/medicines', medicineData),

  getPublicSettings: () =>
    api.get('/admin/public-settings'),

  // Clinic Branding & App Settings
  getClinicBranding: () => {
    // Check cache first
    if (clinicBrandingCache && (Date.now() - clinicBrandingCache.timestamp) < CACHE_DURATION) {
      console.log('📦 Using cached clinic branding data');
      return Promise.resolve({ data: clinicBrandingCache.data });
    }

    // Generate a unique key for this specific request
    const requestKey = 'GET:/admin/clinic-branding';

    return requestDeduplicator.deduplicate(requestKey, async () => {
      console.log('🌐 Fetching fresh clinic branding data');
      try {
        const response = await api.get('/admin/clinic-branding');

        // Cache the response
        clinicBrandingCache = {
          data: response.data,
          timestamp: Date.now()
        };

        return response;
      } catch (error: any) {
        console.warn('Backend clinic branding failed, using defaults');
        console.error('Clinic branding error details:', {
          message: error?.message,
          response: error?.response?.data,
          status: error?.response?.status,
          code: error?.code,
          stack: error?.stack
        });

        // Return default clinic data when backend fails
        const defaultData = {
          CLINIC_NAME: 'Modern Clinic Management System',
          CLINIC_TAGLINE: 'Healthcare Management System',
          CLINIC_LOGO: null,
          CLINIC_ADDRESS: '123 Healthcare Street, Medical City',
          CLINIC_PHONE: '+1 (555) 123-4567',
          CLINIC_EMAIL: 'info@modernclinic.com',
          CLINIC_WEBSITE: 'https://modernclinic.com'
        };

        // Cache the default data
        clinicBrandingCache = {
          data: defaultData,
          timestamp: Date.now()
        };

        return { data: defaultData };
      }
    });
  },

  updateClinicSettings: (settingsData: any) => {
    // Clear cache when settings are updated
    clinicBrandingCache = null;
    return api.put('/admin/clinic-settings', settingsData);
  },

  clearClinicCache: () => {
    // Clear cache to force fresh data fetch
    clinicBrandingCache = null;
    console.log('🧹 Clinic branding cache cleared');
  },

  uploadLogo: (logoFile: FormData) => {
    // Clear cache when logo is updated
    clinicBrandingCache = null;
    return api.post('/admin/upload-logo', logoFile, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },

  // Clear clinic branding cache (useful for debugging)
  clearClinicBrandingCache: () => {
    clinicBrandingCache = null;
    console.log('🗑️ Clinic branding cache cleared');
  },

  downloadBackup: () =>
    api.get('/admin/backup-database', { responseType: 'blob', timeout: 120000 }),

  // Server-side backup management
  saveBackupToServer: () =>
    api.post('/admin/server-backups'),

  listServerBackups: () =>
    api.get('/admin/server-backups'),

  deleteServerBackup: (filename: string) =>
    api.delete(`/admin/server-backups/${encodeURIComponent(filename)}`),
};

// Insurance API
export const insuranceAPI = {
  getProviders: (params?: any) =>
    api.get('/insurance/providers', { params }),

  getProvider: (id: string) =>
    api.get(`/admin/insurance-providers/${id}`),

  createProvider: (providerData: any) =>
    api.post('/admin/insurance-providers', providerData),

  updateProvider: (id: string, providerData: any) =>
    api.put(`/admin/insurance-providers/${id}`, providerData),

  toggleProviderStatus: (id: string, isActive: boolean) =>
    api.patch(`/admin/insurance-providers/${id}/toggle-status`, { isActive }),

  deleteProvider: (id: string) =>
    api.delete(`/admin/insurance-providers/${id}`),

  getCategories: (params?: any) =>
    api.get('/insurance/categories', { params }),

  getProviderCategories: (providerId: string) =>
    api.get(`/insurance/providers/${providerId}/categories`),

  createCategory: (categoryData: any) =>
    api.post('/insurance/categories', categoryData),

  updateCategory: (id: string, categoryData: any) =>
    api.put(`/insurance/categories/${id}`, categoryData),

  getCoverageRules: (params?: any) =>
    api.get('/insurance/coverage-rules', { params }),

  createCoverageRule: (ruleData: any) =>
    api.post('/insurance/coverage-rules', ruleData),

  updateCoverageRule: (id: string, ruleData: any) =>
    api.put(`/insurance/coverage-rules/${id}`, ruleData),

  deleteCoverageRule: (id: string) =>
    api.delete(`/insurance/coverage-rules/${id}`),

  checkCoverage: (coverageData: any) =>
    api.post('/insurance/check-coverage', coverageData),

  // New verification endpoints
  getVerifications: (params?: any) =>
    api.get('/insurance/verifications', { params }),

  getVerification: (id: string) =>
    api.get(`/insurance/verifications/${id}`),

  verifyInsurance: (id: string, verificationData: any) =>
    api.post(`/insurance/verifications/${id}/verify`, verificationData),

  // Check coverage for specific service
  checkServiceCoverage: (policyId: string, serviceData: any) =>
    api.post(`/insurance/policies/${policyId}/check-coverage`, serviceData),

  // Get patient insurance information
  getPatientInsurance: (patientId: string) =>
    api.get(`/insurance/patients/${patientId}/policies`),
};

// Workflow API
export const workflowAPI = {
  getWorkflows: (params?: any) => api.get('/workflows', { params }),
  getScheduledPatients: (params?: any) => api.get('/workflows/scheduled', { params }),
  getTriagePatients: (params?: any) => api.get('/workflows/triage', { params }),
  checkInPatient: (checkInData: any) => api.post('/workflows/checkin', checkInData),
  moveToWaiting: (id: string, notes?: string) => api.post(`/workflows/${id}/move-to-waiting`, { notes }),
  updateWorkflowStatus: (id: string, statusData: any) => api.put(`/workflows/${id}/status`, statusData),
  updateWorkflow: (id: string, data: any) => api.put(`/workflows/${id}`, data),
  assignDoctor: (id: string, doctorId: string) => api.post(`/workflows/${id}/assign-doctor`, { doctorId }),
  getWorkflow: (id: string) => api.get(`/workflows/${id}`),

  // Vital Signs
  recordVitalSigns: (id: string, vitalsData: any) => api.post(`/workflows/${id}/vitals`, vitalsData),
  createWorkflowVitals: (id: string, vitalsData: any) => api.post(`/workflows/${id}/vitals`, vitalsData),

  // SOAP Notes
  getSOAPNotes: (id: string) => api.get(`/workflows/${id}/soap`),
  getWorkflowSOAP: (id: string) => api.get(`/workflows/${id}/soap`),
  recordSOAPNotes: (id: string, soapData: any) => api.post(`/workflows/${id}/soap`, soapData),
  createWorkflowSOAP: (id: string, soapData: any) => api.post(`/workflows/${id}/soap`, soapData),

  // Lab Orders
  addLabOrder: (id: string, labData: any) => api.post(`/workflows/${id}/lab-orders`, labData),
  createWorkflowLabOrder: (id: string, labData: any) => api.post(`/workflows/${id}/lab-orders`, labData),
  processLabResults: (id: string, labResultsData: any) => api.post(`/workflows/${id}/lab-results`, labResultsData),

  // Prescriptions
  addPrescription: (id: string, prescriptionData: any) => api.post(`/workflows/${id}/prescriptions`, prescriptionData),
  createWorkflowPrescription: (id: string, prescriptionData: any) => api.post(`/workflows/${id}/prescriptions`, prescriptionData),

  // Consultation
  endConsultation: (id: string, consultationData?: any) => api.post(`/workflows/${id}/end-consultation`, consultationData || {}),
  cancelConsultation: (id: string, reason?: string) => api.post(`/workflows/${id}/cancel-consultation`, { reason }),

  // Discharge
  dischargePatient: (id: string, dischargeData: any) => api.post(`/workflows/${id}/discharge`, dischargeData),

  // Patient checkout
  patientCheckout: (id: string, checkoutData: any) => api.post(`/workflows/${id}/patient-checkout`, checkoutData),
  generateVisitReport: (id: string, format?: string) => api.get(`/workflows/${id}/visit-report`, { params: { format } }),
  // Get all CHECKOUT workflows with comprehensive data
  getCheckoutWorkflows: (params?: any) => api.get('/workflows/checkout', { params }),

  // Insurance validation (preview coverage before checkout)
  validateInsuranceCoverage: (id: string, coverageData: any) => api.post(`/workflows/${id}/validate-coverage`, coverageData),

  // Insurance eligibility verification
  verifyInsuranceEligibility: (id: string, eligibilityData: any) => api.post(`/workflows/${id}/verify-eligibility`, eligibilityData),

  // Get counts for dashboard badges
  getWaitingPatientsCount: () => api.get('/workflows/counts/waiting', { timeout: 30000 }), // 30 seconds timeout
  getTriagePatientsCount: () => api.get('/workflows/counts/triage', { timeout: 30000 }), // 30 seconds timeout
  getScheduledPatientsCount: () => api.get('/workflows/counts/scheduled', { timeout: 30000 }), // 30 seconds timeout
};

// Nurse API
export const nurseAPI = {
  getDashboard: () =>
    api.get('/nurses/dashboard'),

  getPatients: (params?: any) =>
    api.get('/nurses/patients', { params }),

  getPatientVitals: (patientId: string) =>
    api.get(`/nurses/patients/${patientId}/vitals`),

  recordVitals: (patientId: string, vitalsData: any) =>
    api.post(`/nurses/patients/${patientId}/vitals`, vitalsData),

  getMedicationTasks: (params?: any) =>
    api.get('/nurses/medication-tasks', { params }),

  updateMedicationTask: (taskId: string, status: string) =>
    api.patch(`/nurses/medication-tasks/${taskId}`, { status }),

  getPatientAssignments: (params?: any) =>
    api.get('/nurses/patient-assignments', { params }),
};

// Lab API
export const labAPI = {
  getDashboard: () =>
    api.get('/lab/dashboard'),

  getTestTypes: (params?: any) =>
    api.get('/lab/test-types', { params }),

  getTestTypesByDepartment: (params?: any) =>
    api.get('/lab/test-types/by-department', { params }),

  getCategories: (params?: any) =>
    api.get('/lab/hierarchy/categories', { params }),

  createCategory: (categoryData: any) =>
    api.post('/lab/hierarchy/categories', categoryData),

  updateCategory: (categoryId: string, categoryData: any) =>
    api.put(`/lab/hierarchy/categories/${categoryId}`, categoryData),

  deleteCategory: (categoryId: string) =>
    api.delete(`/lab/hierarchy/categories/${categoryId}`),

  getTestCategories: (params?: any) =>
    api.get('/lab/test-types/categories', { params }),

  addTestCategory: (categoryData: any) =>
    api.post('/lab/test-types/categories', categoryData),

  updateTestCategory: (categoryData: any) =>
    api.put('/lab/test-types/categories', categoryData),

  getCategoryPrice: (categoryId: string, patientId?: string) =>
    api.post('/lab/category-price', { categoryId, patientId }),

  getTestPrice: (testTypeId: string, patientId?: string) =>
    api.post('/lab/test-price', { testTypeId, patientId }),

  // Removed getSubcategories - subcategories no longer used
  // Tests are now directly under categories

  getTestsByCategory: (categoryId: string, params?: any) =>
    api.get(`/lab/hierarchy/categories/${categoryId}/tests`, { params }),

  searchTestTypes: (query: string, limit: number = 20) =>
    api.get('/lab/test-types', { params: { search: query, limit } }),

  getTestDepartments: (params?: any) =>
    api.get('/lab/test-types/departments', { params }),

  addTestDepartment: (departmentData: any) =>
    api.post('/lab/test-types/departments', departmentData),

  updateTestDepartment: (departmentData: any) =>
    api.put('/lab/test-types/departments', departmentData),

  getTestType: (id: string) =>
    api.get(`/lab/test-types/${id}`),

  createTestType: (testTypeData: any) =>
    api.post('/lab/test-types', testTypeData),

  updateTestType: (id: string, testTypeData: any) =>
    api.put(`/lab/test-types/${id}`, testTypeData),

  deleteTestType: (id: string) =>
    api.delete(`/lab/test-types/${id}`),

  getNormalsByTest: async (testId: string) => {
    // Fetch test type which includes normals
    const response = await api.get(`/lab/test-types/${testId}`);
    if (response.data?.success && response.data.data?.normals) {
      return { data: { success: true, data: response.data.data.normals } };
    }
    return { data: { success: false, data: [] } };
  },

  // Rate management
  getTestTypeRates: (testTypeId: string) =>
    api.get(`/lab/test-types/${testTypeId}/rates`),

  upsertTestTypeRate: (testTypeId: string, rateData: any) =>
    api.post(`/lab/test-types/${testTypeId}/rates`, rateData),

  deleteTestTypeRate: (rateId: string) =>
    api.delete(`/lab/rates/${rateId}`),

  getTestTypeWithRates: (testTypeId: string) =>
    api.get(`/lab/test-types/${testTypeId}/rates`),

  // Category rate management
  getCategoryRates: (categoryId: string) =>
    api.get(`/lab/hierarchy/categories/${categoryId}/rates`),

  upsertCategoryRate: (categoryId: string, rateData: any) =>
    api.post(`/lab/hierarchy/categories/${categoryId}/rates`, rateData),

  deleteCategoryRate: (rateId: string) =>
    api.delete(`/lab/hierarchy/categories/rates/${rateId}`),

  getLabOrders: (params?: any) =>
    api.get('/lab/orders', { params }),

  getLabOrder: (id: string) =>
    api.get(`/lab/orders/${id}`),

  createLabOrder: (orderData: any) =>
    api.post('/lab/orders', orderData),

  addTestsToOrder: (orderId: string, testsData: any) =>
    api.post(`/lab/orders/${orderId}/tests`, testsData),

  getPatientLabOrders: (patientId: string, params?: any) =>
    api.get(`/lab/patients/${patientId}/orders`, { params }),

  getAdmissionLabOrders: (admissionId: string, params?: any) =>
    api.get(`/lab/admissions/${admissionId}/orders`, { params }),

  createAdmissionLabOrder: (admissionId: string, orderData: any) =>
    api.post(`/lab/admissions/${admissionId}/orders`, orderData),

  createDynamicLabOrder: (orderData: any) =>
    api.post('/lab/orders/dynamic', orderData),

  updateLabOrder: (id: string, orderData: any) =>
    api.put(`/lab/orders/${id}`, orderData),

  processLabOrder: (id: string, resultData: any) =>
    api.post(`/lab/orders/${id}/process`, resultData),

  getTestResults: (params?: any) =>
    api.get('/lab/results', { params }),

  getTestResult: (id: string) =>
    api.get(`/lab/results/${id}`),

  createTestResult: (resultData: any) =>
    api.post('/lab/results', resultData),

  updateTestResult: (id: string, resultData: any) =>
    api.put(`/lab/results/${id}`, resultData),

  getEquipmentStatus: () =>
    api.get('/lab/equipment/status'),

  updateEquipmentStatus: (equipmentId: string, statusData: any) =>
    api.patch(`/lab/equipment/${equipmentId}/status`, statusData),

  getQueueStats: (params?: { dateFrom?: string; serviceType?: string }) =>
    api.get('/lab/queue/stats', { params }),

  getLaboratoryReport: (params?: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    insuranceProvider?: string;
    category?: string;
    search?: string;
    page?: number;
    limit?: number;
    exportAll?: string;
  }) => api.get('/lab/reports/laboratory', { params }),

  getLaboratoryReportSummary: (params?: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    insuranceProvider?: string;
    category?: string;
    search?: string;
  }) => api.get('/lab/reports/laboratory/summary', { params }),

  getLaboratoryReportExport: (params?: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    insuranceProvider?: string;
    category?: string;
    search?: string;
  }) => api.get('/lab/reports/laboratory', { params: { ...params, exportAll: 'true' } }),

  getRadiologyReport: (params?: {
    dateFrom?: string;
    dateTo?: string;
    status?: string;
    insuranceProvider?: string;
    category?: string;
    search?: string;
  }) => api.get('/lab/reports/radiology', { params }),

  getLaboratoryReportFilters: () => api.get('/lab/reports/laboratory/filters'),

  getRadiologyReportFilters: () => api.get('/lab/reports/radiology/filters'),

  getPendingOrders: (params?: any) =>
    api.get('/lab/orders/pending', { params }),

  getPendingApprovalOrders: () =>
    api.get('/lab/orders/pending-approval'),

  approveLabOrder: (orderId: string) =>
    api.put(`/lab/orders/${orderId}/approve`),

  bulkApproveLabOrders: (orderIds: string[]) =>
    api.post('/lab/orders/bulk-approve', { orderIds }),

  rejectLabOrder: (orderId: string) =>
    api.delete(`/lab/orders/${orderId}/reject`),

  deleteLabOrder: (id: string) =>
    api.delete(`/lab/orders/${id}`),

  // Enter lab test result for a specific item
  enterTestResult: (orderId: string, itemId: string, resultData: any) =>
    api.post(`/lab/orders/${orderId}/items/${itemId}/result`, resultData),

  // Get lab test results for a specific order
  getLabOrderResults: (orderId: string) =>
    api.get(`/lab/orders/${orderId}/results`),

  // Update lab order item result (existing method)
  updateLabOrderItemResult: (orderId: string, itemId: string, resultData: any) =>
    api.put(`/lab/orders/${orderId}/items/${itemId}/result`, resultData),

  // Verification operations
  verifyLabOrder: (id: string, verificationData: any) =>
    api.patch(`/lab/orders/${id}/verify`, verificationData),

  rejectLabOrderVerification: (id: string, verificationData: any) =>
    api.patch(`/lab/orders/${id}/reject`, verificationData),

  // Comprehensive Lab Orders Management
  updateTestStatus: (orderId: string, itemId: string, statusData: any) =>
    api.put(`/lab/orders/${orderId}/items/${itemId}/status`, statusData),

  updateOrderStatus: (orderId: string, statusData: any) =>
    api.put(`/lab/orders/${orderId}/status`, statusData),

  bulkUpdateOrders: (orderIds: string[], actionData: any) =>
    api.post('/lab/orders/bulk-update', { orderIds, ...actionData }),

  // Reconciliation
  getReconciliations: (params?: any) =>
    api.get('/lab/reconciliations', { params }),

  createReconciliation: (data: any) =>
    api.post('/lab/reconciliations', data),
};

// Staff API
export const staffAPI = {
  getDashboard: () =>
    api.get('/staff/dashboard'),

  getReceptionistDashboard: () =>
    api.get('/staff/receptionist-dashboard'),

  getAccountantDashboard: () =>
    api.get('/staff/accountant-dashboard'),

  getStaff: (params?: any) =>
    api.get('/staff', { params, timeout: 30000 }), // 30 seconds timeout for staff list

  getStaffMember: (id: string) =>
    api.get(`/staff/${id}`, { timeout: 30000 }), // 30 seconds timeout for staff details

  createStaffMember: (staffData: any) =>
    api.post('/staff', staffData),

  updateStaffMember: (id: string, staffData: any) =>
    api.put(`/staff/${id}`, staffData),

  deleteStaffMember: (id: string) =>
    api.delete(`/staff/${id}`),

  createStaff: (data: any) =>
    api.post('/staff', data),

  updateStaff: (id: string, data: any) =>
    api.put(`/staff/${id}`, data),

  getSchedule: (staffId: string) =>
    api.get(`/staff/${staffId}/schedule`),

  getStaffSchedule: (staffId: string) =>
    api.get(`/staff/${staffId}/schedule`),

  getDoctorSchedules: (params: any) =>
    api.get('/staff/doctors/schedules', { params }),

  updateSchedule: (staffId: string, scheduleData: any) =>
    api.put(`/staff/${staffId}/schedule`, scheduleData),

  updateDoctorSchedule: (doctorId: string, scheduleData: any) =>
    api.put(`/staff/doctors/${doctorId}/schedule`, scheduleData),

  updateStaffSchedule: (staffId: string, scheduleData: any) =>
    api.put(`/staff/${staffId}/schedule`, scheduleData),

  getAttendance: (staffId: string, params?: any) =>
    api.get(`/staff/${staffId}/attendance`, { params }),

  recordAttendance: (staffId: string, attendanceData: any) =>
    api.post(`/staff/${staffId}/attendance`, attendanceData),

  // Form data APIs
  getDepartments: () =>
    api.get('/admin/departments/public'),

  // Staff Department Assignment
  assignDepartment: (staffId: string, departmentId: string, isPrimary: boolean = false) =>
    api.post(`/staff/${staffId}/departments`, { departmentId, isPrimary }),

  removeDepartmentAssignment: (staffId: string, assignmentId: string) =>
    api.delete(`/staff/${staffId}/departments/${assignmentId}`),

  setPrimaryDepartment: (staffId: string, assignmentId: string) =>
    api.patch(`/staff/${staffId}/departments/${assignmentId}/primary`),

  // Role Management
  getRoles: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    status?: 'all' | 'active' | 'inactive' | 'system';
    sortBy?: 'name' | 'createdAt' | 'userCount';
    sortOrder?: 'asc' | 'desc';
  }) =>
    api.get('/roles', { params }),
  getRole: (id: string) =>
    api.get(`/roles/${id}`),
  createRole: (roleData: any) =>
    api.post('/roles', roleData),
  updateRole: (id: string, roleData: any) =>
    api.put(`/roles/${id}`, roleData),
  deleteRole: (id: string) =>
    api.delete(`/roles/${id}`),
  assignRole: (userId: string, roleId: string) =>
    api.post('/roles/assign', { userId, roleId }),
  bulkAssignRoles: (assignments: Array<{ userId: string, roleId: string }>) =>
    api.post('/roles/bulk-assign', { assignments }),
  getRolePermissions: (roleId: string) =>
    api.get(`/roles/${roleId}/permissions`),
  updateRolePermissions: (roleId: string, permissionIds: string[]) =>
    api.put(`/roles/${roleId}/permissions`, { permissionIds }),
  getUsersByRole: (roleId: string, page: number = 1, limit: number = 10) =>
    api.get(`/roles/${roleId}/users`, { params: { page, limit } }),
  getUserPermissions: () =>
    api.get('/auth/my-permissions', { timeout: 30000 }), // 30 seconds timeout for permissions

  // Permission Management
  getPermissions: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    resource?: string;
    action?: string;
  }) =>
    api.get('/permissions', { params }),
  getPermission: (id: string) =>
    api.get(`/permissions/${id}`),
  createPermission: (permissionData: any) =>
    api.post('/permissions', permissionData),
  updatePermission: (id: string, permissionData: any) =>
    api.put(`/permissions/${id}`, permissionData),
  deletePermission: (id: string) =>
    api.delete(`/permissions/${id}`),
  getResourcesAndActions: () =>
    api.get('/permissions/resources-actions'),
};

// Server Time API
export const serverTimeAPI = {
  getServerTime: () =>
    api.get('/server-time', { timeout: 15000 }), // 15 seconds timeout for server time
};

// Medical Records API
export const medicalRecordAPI = {
  getMedicalRecords: (params?: any) =>
    api.get('/medical-records', { params }),

  getMedicalRecord: (id: string) =>
    api.get(`/medical-records/${id}`),

  createMedicalRecord: (recordData: any) =>
    api.post('/medical-records', recordData),

  updateMedicalRecord: (id: string, recordData: any) =>
    api.put(`/medical-records/${id}`, recordData),

  uploadAttachment: (recordId: string, file: FormData) =>
    api.post(`/medical-records/${recordId}/attachments`, file),

  downloadAttachment: (recordId: string, attachmentId: string) =>
    api.get(`/medical-records/${recordId}/attachments/${attachmentId}`, { responseType: 'blob' }),
};

// Pharmacy API
export const pharmacyAPI = {
  getMedicines: (params?: any) => {
    const key = requestDeduplicator.generateKey('GET', '/pharmacy/medicines', params);
    return requestDeduplicator.deduplicate(key, () =>
      api.get('/pharmacy/medicines', { params, timeout: 30000 }) // 30 seconds timeout for medicines
    );
  },

  getMedicine: (id: string) =>
    api.get(`/pharmacy/medicines/${id}`),

  createMedicine: (medicineData: any) =>
    api.post('/pharmacy/medicines', medicineData),

  importMedicines: (
    medicines: Array<{ id?: string; name?: string; stockQuantity: number }>,
    reason?: string
  ) =>
    api.post('/pharmacy/medicines/import', { medicines, reason }, { timeout: 60000 }),

  updateMedicine: (id: string, medicineData: any) =>
    api.put(`/pharmacy/medicines/${id}`, medicineData),

  updateStock: (id: string, stockData: any) =>
    api.post(`/pharmacy/medicines/${id}/stock`, stockData),

  getMedicineBatches: (medicineId: string) =>
    api.get(`/pharmacy/medicines/${medicineId}/batches`),

  addMedicineBatch: (medicineId: string, batchData: any) =>
    api.post(`/pharmacy/medicines/${medicineId}/batches`, batchData),

  getStockMovementsReport: (params?: any) =>
    api.get('/pharmacy/reports/stock-movements', { params, timeout: 60000 }),

  getPrescriptions: (params?: any) => {
    const key = requestDeduplicator.generateKey('GET', '/pharmacy/prescriptions', params);
    return requestDeduplicator.deduplicate(key, () =>
      api.get('/pharmacy/prescriptions', { params, timeout: 30000 }) // 30 seconds timeout for prescriptions
    );
  },

  getPrescription: (id: string) =>
    api.get(`/pharmacy/prescriptions/${id}`),

  createPrescription: (prescriptionData: any) =>
    api.post('/pharmacy/prescriptions', prescriptionData),

  updatePrescription: (id: string, prescriptionData: any) =>
    api.put(`/pharmacy/prescriptions/${id}`, prescriptionData),

  refillPrescription: (id: string, refillData: any) =>
    api.post(`/pharmacy/prescriptions/${id}/refill`, refillData),

  getActivePrescriptions: (patientId: string) =>
    api.get(`/pharmacy/prescriptions/active/${patientId}`),

  getExpiringPrescriptions: (days?: number) =>
    api.get('/pharmacy/prescriptions/expiring', { params: { days } }),

  getInventoryLogs: (params?: any) =>
    api.get('/pharmacy/inventory-logs', { params }),

  getMedicineSalesReport: (params?: any) =>
    api.get('/pharmacy/reports/medicine-sales', { params, timeout: 60000 }),

  getReconciliations: (params?: any) =>
    api.get('/pharmacy/reconciliations', { params }),

  createReconciliation: (data: any) =>
    api.post('/pharmacy/reconciliations', data),

  getMedicineStock: (id: string) =>
    api.get(`/pharmacy/medicines/${id}/stock`),

  // Rate management
  getMedicineRates: (medicineId: string) =>
    api.get(`/pharmacy/medicines/${medicineId}/rates`),

  upsertMedicineRate: (medicineId: string, rateData: any) =>
    api.post(`/pharmacy/medicines/${medicineId}/rates`, rateData),

  deleteMedicineRate: (rateId: string) =>
    api.delete(`/pharmacy/medicine-rates/${rateId}`),

  getMedicinePrice: (medicineId: string, patientId: string) =>
    api.post('/pharmacy/pharm-price', { medicineId, patientId }),

  // Patient search for pharmacy prescription creation
  searchPatients: (query: string, searchBy: 'name' | 'phone' | 'bed' = 'name', limit: number = 10, page: number = 1) =>
    api.get('/pharmacy/patients/search', {
      params: {
        q: query,
        by: searchBy,
        limit,
        page
      }
    }),

  // Get all pharmacy dashboard data in one call
  getDashboard: async (params?: any) => {
    try {
      const response = await api.get('/pharmacy/dashboard', { params });
      return response;
    } catch (error) {
      console.error('Error fetching pharmacy dashboard:', error);
      throw error;
    }
  },

  // Medicine Categories Management
  getCategories: () =>
    api.get('/pharmacy/categories'),

  createCategory: (categoryData: { name: string; description?: string }) =>
    api.post('/pharmacy/categories', categoryData),

  updateCategory: (id: string, categoryData: { name: string; description?: string }) =>
    api.put(`/pharmacy/categories/${id}`, categoryData),

  deleteCategory: (id: string) =>
    api.delete(`/pharmacy/categories/${id}`),

  // Medicine Units Management
  getUnits: () =>
    api.get('/pharmacy/units'),

  createUnit: (unitData: { name: string; description?: string }) =>
    api.post('/pharmacy/units', unitData),
};

// Prescription API
export const prescriptionAPI = {
  // Get all prescriptions with filtering and pagination
  getPrescriptions: (params?: any) =>
    api.get('/pharmacy/prescriptions', { params, timeout: 30000 }), // 30 seconds timeout for prescriptions

  // Get prescription details for viewing
  getPrescription: (id: string) =>
    api.get(`/pharmacy/prescriptions/${id}`),

  // Create new prescription
  createPrescription: (data: any) =>
    api.post('/pharmacy/prescriptions', data),

  // Create workflow prescription
  createWorkflowPrescription: (data: any) =>
    api.post('/pharmacy/prescriptions/workflow', data),

  // Add prescription (alias for createPrescription)
  addPrescription: (data: any) =>
    api.post('/pharmacy/prescriptions', data),

  // Create (alias for createPrescription)
  create: (data: any) =>
    api.post('/pharmacy/prescriptions', data),

  // Update prescription
  updatePrescription: (id: string, data: any) =>
    api.put(`/pharmacy/prescriptions/${id}`, data),

  // Delete prescription
  deletePrescription: (id: string) =>
    api.delete(`/pharmacy/prescriptions/${id}`),

  // Process prescription (change status to PROCESSING)
  processPrescription: (id: string, data: any) =>
    api.patch(`/pharmacy/prescriptions/${id}/process`, data),

  // Update prescription status
  updatePrescriptionStatus: (id: string, data: any) =>
    api.patch(`/pharmacy/prescriptions/${id}/status`, data),

  // Fulfill prescription (dispense medicines and update stock)
  fulfillPrescription: (id: string, data: any) =>
    api.post(`/pharmacy/prescriptions/${id}/fulfill`, data),

  // Dispense admission medication (new system)
  dispenseAdmissionMedication: (id: string, data: any) =>
    api.post(`/pharmacy/dispensings/${id}/dispense`, data),

  // Get prescription print data
  getPrescriptionPrint: (id: string) =>
    api.get(`/pharmacy/prescriptions/${id}/print`),

  // Get patient prescription history
  getPatientPrescriptions: (patientId: string, params?: any) =>
    api.get(`/pharmacy/patients/${patientId}/prescriptions`, { params }),

  // Verification operations
  verifyPrescription: (id: string, verificationData: any) =>
    api.patch(`/pharmacy/prescriptions/${id}/verify`, verificationData),

  rejectPrescription: (id: string, verificationData: any) =>
    api.patch(`/pharmacy/prescriptions/${id}/reject`, verificationData),
};

// Reports API
export const reportAPI = {
  getDailyCollections: (params?: any) =>
    api.get('/reports/daily-collections', { params }),

  getInsurancePending: (params?: any) =>
    api.get('/reports/insurance-pending', { params }),

  getDoctorEarnings: (params?: any) =>
    api.get('/reports/doctor-earnings', { params }),

  getFraudAudit: (params?: any) =>
    api.get('/reports/fraud-audit', { params }),

  getFinancialSummary: (params?: any) =>
    api.get('/reports/financial-summary', { params }),

  getConsultations: (params?: any) =>
    api.get('/reports/consultations', { params }),

  getDoctorsSoapSummary: (params?: any) =>
    api.get('/reports/doctors-soap/summary', { params }),

  getDoctorsSoapPage: (params?: any) =>
    api.get('/reports/doctors-soap', { params }),

  getDoctorsSoapExport: (params?: any) =>
    api.get('/reports/doctors-soap', { params: { ...params, exportAll: 'true' } }),

  getReconciliations: (params?: any) =>
    api.get('/reports/reconciliations', { params }),

  createReconciliation: (data: any) =>
    api.post('/reports/reconciliations', data),
};

// Inventory API
export const inventoryAPI = {
  getItems: (params?: any) =>
    api.get('/inventory/items', { params }),

  getItem: (id: string) =>
    api.get(`/inventory/items/${id}`),

  createItem: (itemData: any) =>
    api.post('/inventory/items', itemData),

  updateItem: (id: string, itemData: any) =>
    api.put(`/inventory/items/${id}`, itemData),

  updateStock: (id: string, stockData: any) =>
    api.post(`/inventory/items/${id}/stock`, stockData),

  getTransactions: (params?: any) =>
    api.get('/inventory/transactions', { params }),

  getSummary: () =>
    api.get('/inventory/summary'),

  bulkUsage: (usageData: any) =>
    api.post('/inventory/bulk-usage', usageData),

  autoDeduct: (deductData: any) =>
    api.post('/inventory/auto-deduct', deductData),

  getLowStockItems: () =>
    api.get('/inventory/items?lowStock=true'),
  getReorderSuggestions: () => api.get('/inventory/reorder-suggestions'),
  getItemsNeedingReorder: () => api.get('/inventory/items-needing-reorder'),
  createPurchaseOrderFromSuggestions: (orderData: any) => api.post('/inventory/create-purchase-order', orderData),
  getValuationReport: (params?: any) => api.get('/inventory/valuation-report', { params }),
  getAuditLogs: (params?: any) => api.get('/inventory/audit-logs', { params }),
  getStorageLocations: () => api.get('/inventory/storage-locations'),
  createStorageLocation: (locationData: any) => api.post('/inventory/storage-locations', locationData),
  // Stock Issuance
  issueStock: (data: any) => api.post('/inventory/issue-stock', data),
  getStockIssuances: (params?: any) => api.get('/inventory/stock-issuances', { params }),
  getStockIssuance: (id: string) => api.get(`/inventory/stock-issuances/${id}`),
};

// Supplier API
export const supplierAPI = {
  getSuppliers: (params?: any) => api.get('/suppliers', { params }),
  getSupplier: (id: string) => api.get(`/suppliers/${id}`),
  createSupplier: (supplierData: any) => api.post('/suppliers', supplierData),
  updateSupplier: (id: string, supplierData: any) => api.put(`/suppliers/${id}`, supplierData),
  deleteSupplier: (id: string) => api.delete(`/suppliers/${id}`), // Soft delete (deactivate)
  permanentlyDeleteSupplier: (id: string) => api.delete(`/suppliers/${id}/permanent`), // Hard delete
  reactivateSupplier: (id: string) => api.patch(`/suppliers/${id}/reactivate`), // Reactivate
  getSupplierPerformance: (id: string, params?: any) => api.get(`/suppliers/${id}/performance`, { params }),
};

// Medicine Batch API
export const medicineBatchAPI = {
  getBatches: (params?: any) => api.get('/medicine-batches', { params }),
  getBatch: (id: string) => api.get(`/medicine-batches/${id}`),
  createBatch: (batchData: any) => api.post('/medicine-batches', batchData),
  updateBatch: (id: string, batchData: any) => api.put(`/medicine-batches/${id}`, batchData),
  issueBatch: (id: string, issueData: any) => api.post(`/medicine-batches/${id}/issue`, issueData),
  getBatchMovements: (id: string, params?: any) => api.get(`/medicine-batches/${id}/movements`, { params }),
  getExpiringBatches: (params?: any) => api.get('/medicine-batches/reports/expiring', { params }),
};

// Purchase Order API
export const purchaseOrderAPI = {
  getPurchaseOrders: (params?: any) => api.get('/purchase-orders', { params }),
  getPurchaseOrder: (id: string) => api.get(`/purchase-orders/${id}`),
  createPurchaseOrder: (orderData: any) => api.post('/purchase-orders', orderData),
  updatePurchaseOrder: (id: string, orderData: any) => api.put(`/purchase-orders/${id}`, orderData),
  addItemToOrder: (id: string, itemData: any) => api.post(`/purchase-orders/${id}/items`, itemData),
  removeItemFromOrder: (id: string, itemId: string) => api.delete(`/purchase-orders/${id}/items/${itemId}`),
  receiveOrder: (id: string, receiveData: any) => api.post(`/purchase-orders/${id}/receive`, receiveData),
  getOrderStatistics: (params?: any) => api.get('/purchase-orders/statistics/summary', { params }),
};

// Diagnosis Codes API
export const diagnosisCodeAPI = {
  getDiagnosisCodes: (params?: any) => api.get('/diagnosis-codes', { params }),
  getDiagnosisCode: (id: string) => api.get(`/diagnosis-codes/${id}`),
  createDiagnosisCode: (codeData: any) => api.post('/diagnosis-codes', codeData),
  updateDiagnosisCode: (id: string, codeData: any) => api.put(`/diagnosis-codes/${id}`, codeData),
  getCategories: () => api.get('/diagnosis-codes/categories/list'),
};

// XClaims API
export const xClaimAPI = {
  create: (data: any) => api.post('/xclaims', data),
  getAll: () => api.get('/xclaims'),
};

// Radiology API (reuses lab endpoints with serviceType=RADIOLOGY)
export const radiologyAPI = {
  // Categories
  getCategories: (params?: any) =>
    api.get('/lab/hierarchy/categories', { params: { ...params, serviceType: 'RADIOLOGY' } }),

  createCategory: (categoryData: any) =>
    api.post('/lab/hierarchy/categories', { ...categoryData, serviceType: 'RADIOLOGY' }),

  updateCategory: (categoryId: string, categoryData: any) =>
    api.put(`/lab/hierarchy/categories/${categoryId}`, categoryData),

  deleteCategory: (categoryId: string) =>
    api.delete(`/lab/hierarchy/categories/${categoryId}`),

  // Test types (radiology procedures)
  getTestTypes: (params?: any) =>
    api.get('/lab/test-types', { params: { ...params, serviceType: 'RADIOLOGY' } }),

  createTestType: (data: any) =>
    api.post('/lab/test-types', { ...data, serviceType: 'RADIOLOGY' }),

  updateTestType: (id: string, data: any) =>
    api.put(`/lab/test-types/${id}`, data),

  deleteTestType: (id: string) =>
    api.delete(`/lab/test-types/${id}`),

  getTestsByCategory: (categoryId: string, params?: any) =>
    api.get(`/lab/hierarchy/categories/${categoryId}/tests`, { params }),

  // Category rates (pricing) — category-level
  getCategoryRates: (categoryId: string) =>
    api.get(`/lab/hierarchy/categories/${categoryId}/rates`),

  upsertCategoryRate: (categoryId: string, rateData: any) =>
    api.post(`/lab/hierarchy/categories/${categoryId}/rates`, rateData),

  deleteCategoryRate: (rateId: string) =>
    api.delete(`/lab/hierarchy/categories/rates/${rateId}`),

  getCategoryPrice: (categoryId: string, patientId?: string) =>
    api.post('/lab/category-price', { categoryId, patientId }),

  getTestPrice: (testTypeId: string, patientId?: string) =>
    api.post('/lab/test-price', { testTypeId, patientId }),

  // Per-test-type rates (individual test pricing)
  getTestTypeRates: (testTypeId: string) =>
    api.get(`/lab/test-types/${testTypeId}/rates`),

  upsertTestTypeRate: (testTypeId: string, rateData: any) =>
    api.post(`/lab/test-types/${testTypeId}/rates`, rateData),

  deleteTestTypeRate: (rateId: string) =>
    api.delete(`/lab/rates/${rateId}`),

  // Orders
  getOrders: (params?: any) =>
    api.get('/lab/orders', { params: { ...params, serviceType: 'RADIOLOGY' } }),

  getOrder: (id: string) =>
    api.get(`/lab/orders/${id}`),

  createOrder: (orderData: any) =>
    api.post('/lab/orders', { ...orderData, serviceType: 'RADIOLOGY' }),

  createDynamicOrder: (orderData: any) =>
    api.post('/lab/orders/dynamic', { ...orderData, serviceType: 'RADIOLOGY' }),

  getPendingApprovalOrders: () =>
    api.get('/lab/orders/pending-approval', { params: { serviceType: 'RADIOLOGY' } }),

  approveOrder: (orderId: string) =>
    api.put(`/lab/orders/${orderId}/approve`),

  rejectOrder: (orderId: string) =>
    api.delete(`/lab/orders/${orderId}/reject`),

  deleteOrder: (id: string) =>
    api.delete(`/lab/orders/${id}`),

  enterResult: (orderId: string, itemId: string, resultData: any) =>
    api.post(`/lab/orders/${orderId}/items/${itemId}/result`, resultData),

  uploadFiles: (formData: FormData) =>
    api.post('/lab/radiology/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }),

  getOrderResults: (orderId: string) =>
    api.get(`/lab/orders/${orderId}/results`),

  updateOrderStatus: (orderId: string, statusData: any) =>
    api.put(`/lab/orders/${orderId}/status`, statusData),

  // Patient search
  searchPatients: (params: any) =>
    api.get('/lab/patients/search', { params }),
};

// Gynecology API
export const gynecologyAPI = {
  saveGynecologyData: (data: any) =>
    api.post('/gynecology', data),

  getGynecologyHistory: (patientId: string) =>
    api.get(`/gynecology/patient/${patientId}`),

  getGynecologyDataByWorkflow: (workflowId: string) =>
    api.get(`/gynecology/workflow/${workflowId}`),
};

export default api;